import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
  Button, TextField,
} from '@mui/material';

/* ============================================================
   Typed delete confirmation.

   Used for both single-record and bulk deletes across Courses,
   Assessments, Enrollees and Trainings. The Submit button stays
   disabled until the user types "delete" (case-insensitive), so a
   destructive action can't be triggered accidentally.

   Props:
     open      bool
     count     number  (entries being deleted — drives entry/entries wording)
     loading   bool    (shows "Deleting…" + locks the buttons)
     onClose   ()      (cancel)
     onConfirm ()      (run the delete — only reachable once "delete" is typed)
   ============================================================ */
export default function DeleteConfirmDialog({ open, count = 1, loading = false, onClose, onConfirm }) {
  const [text, setText] = useState('');

  // reset the field every time the dialog opens
  useEffect(() => { if (open) setText(''); }, [open]);

  const confirmed = text.trim().toLowerCase() === 'delete';
  const word = count > 1 ? 'entries' : 'entry';

  const submit = () => { if (confirmed && !loading) onConfirm(); };

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 800 }}>Confirm delete</DialogTitle>
      <DialogContent dividers>
        <DialogContentText sx={{ mb: 2 }}>
          To delete the {count} selected {word}, please type <b>delete</b> below.
          This action cannot be undone.
        </DialogContentText>
        <TextField
          autoFocus
          fullWidth
          size="small"
          placeholder="Type delete to confirm"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
          disabled={loading}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} variant="outlined" disabled={loading}>Cancel</Button>
        <Button onClick={submit} variant="contained" color="error" disabled={!confirmed || loading}>
          {loading ? 'Deleting…' : 'Submit'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
